import { Ionicons } from '@expo/vector-icons';
import React from "react";
import {
  View,
  Text,
  FlatList,
  ActivityIndicator,
  ListRenderItem,
  StyleSheet,
} from "react-native";

import { GameRoom } from "../../../features/quiz-core/multiplayerStore";
import { t } from '../../../i18n/translations';
import { iqesColors } from "../../../shared/ui/iqesDesignSystem";
import { useLanguageStore } from '../../quiz-core/languageStore';

interface ActiveRoomsListProps {
  rooms: GameRoom[];
  loading: boolean;
  userId: string;
  renderLobby: ListRenderItem<GameRoom>;
  sectionPaddingBottom?: number;
  groupTypeLabelLower?: string;
  emptyTitle?: string;
  emptySubtitle?: string;
}

const ActiveRoomsList: React.FC<ActiveRoomsListProps> = ({
  rooms,
  loading,
  userId: _userId,
  renderLobby,
  sectionPaddingBottom = 96,
  groupTypeLabelLower = t('familiesFriends.groupTypeFamily', 'en').toLowerCase(),
  emptyTitle,
  emptySubtitle,
}) => {
  const { language } = useLanguageStore();

  return (
    <View style={styles.container} data-oid="a-sx5uo">
      {loading ? (
        <View style={styles.loadingWrap}>
          <ActivityIndicator style={styles.spinner} color={iqesColors.goldBright} data-oid="a2_fwkr" />
          <Text style={styles.loadingText}>
            {t('multiplayerLobby.loadingRooms')}
          </Text>
        </View>
      ) : rooms.length === 0 ? (
        <View
          style={styles.emptyState}
          data-oid=".y6cr34"
        >
          <View style={styles.emptyIconWrap}>
            <Ionicons name="people-outline" size={32} color={iqesColors.goldBright} data-oid="uv27tg." />
          </View>
          <Text
            style={styles.emptyTitle}
            data-oid=":1axc8v"
          >
            {emptyTitle || t('multiplayerLobby.noActiveRooms')}
          </Text>
          <Text style={styles.emptySubtitle}>
            {(emptySubtitle || t('multiplayer_lobby.realTimeGameDescription', language)).replace('{{groupType}}', groupTypeLabelLower)}
          </Text>
        </View>
      ) : (
        <FlatList
          data={rooms}
          keyExtractor={(item) => item.id}
          renderItem={renderLobby}
          contentContainerStyle={[styles.listContent, { paddingBottom: sectionPaddingBottom }]}
          scrollEnabled={false}
          data-oid=".-u86fv"
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: 128,
  },
  loadingWrap: {
    marginTop: 28,
    alignItems: 'center',
  },
  spinner: {
    marginBottom: 10,
  },
  loadingText: {
    color: iqesColors.mutedWarm,
    fontSize: 14,
    fontWeight: '700',
  },
  emptyState: {
    marginTop: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 24,
    borderWidth: 1,
    backgroundColor: 'rgba(8, 10, 24, 0.9)',
    borderColor: 'rgba(246, 190, 58, 0.34)',
    paddingHorizontal: 24,
    paddingVertical: 28,
    shadowColor: iqesColors.gold,
    shadowOpacity: 0.12,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    elevation: 6,
  },
  emptyIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    backgroundColor: 'rgba(246, 190, 58, 0.16)',
    borderWidth: 1,
    borderColor: 'rgba(246, 190, 58, 0.32)',
  },
  emptyTitle: {
    color: iqesColors.ivory,
    fontSize: 18,
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 8,
  },
  emptySubtitle: {
    color: iqesColors.mutedWarm,
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
  listContent: {
    paddingBottom: 96,
  },
});

export default ActiveRoomsList;
